import { getToken } from '../tokenUtils';
import { redirectToPlumApp } from '../redirectToPlumApp';
import { logger } from '../logger';
import AppConfig from '@/AppConfig';

const TOKEN_NOT_FOUND_ERROR = 'Token not found in cookies';

const interceptRequest = async (
  input: RequestInfo | URL,
  init?: RequestInit,
  isDev?: boolean,
): Promise<{ input: RequestInfo | URL; init: RequestInit }> => {
  const headers = new Headers(init?.headers);

  // Check if we should skip token injection
  if (headers.get('x-plum-token') === 'skip') {
    headers.delete('x-plum-token');
    return {
      input,
      init: {
        ...init,
        headers,
      },
    };
  }

  // Get and validate token
  const xPlumToken = getToken();
  if (!xPlumToken) {
    throw new Error(TOKEN_NOT_FOUND_ERROR);
  }

  // Set authentication token
  headers.set('x-plum-token', xPlumToken);

  // Add internal token for dev environment
  if (isDev) {
    const internalToken = AppConfig.INTERNAL_TOKEN;
    if (internalToken) {
      headers.set('x-plum-internal-token', internalToken);
    } else {
      window.dev.e('Internal token not found in AppConfig');
    }
  }

  return {
    input,
    init: {
      ...init,
      headers,
    },
  };
};

const interceptResponse = async (response: Response): Promise<Response> => {
  // Handle 401 unauthorized responses
  if (response.status === 401) {
    window.dev.e('401 Unauthorized response received');
    logger.error('401 Unauthorized response received');

    if (typeof window?.NativeBridge?.onNativeLogout === 'function') {
      window?.NativeBridge?.onNativeLogout();
    } else {
      const redirectToUrl = encodeURI(window.location.href);
      redirectToPlumApp(`/logout?redirectTo=${redirectToUrl}`)();
    }

    // Return the response as-is, let the calling code handle the 401
    return response;
  }

  return response;
};

const handleFetchError = (error: Error): Promise<never> => {
  window.dev.e('Error in fetch interceptor', error);
  logger.error('Error in fetch interceptor', error);

  // Handle token not found error specifically
  if (error.message === TOKEN_NOT_FOUND_ERROR) {
    window.dev.e('Token not found error');

    if (typeof window?.NativeBridge?.onNativeLogout === 'function') {
      window?.NativeBridge?.onNativeLogout();
    } else {
      const redirectToUrl = encodeURI(window.location.href);
      redirectToPlumApp(`/logout?redirectTo=${redirectToUrl}`)();
    }
  }

  return Promise.reject(error);
};

// Alternative approach: Create a wrapper function instead of overriding global fetch
export const createInterceptedFetch = (isDev?: boolean) => {
  return async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    try {
      const config = await interceptRequest(input, init, isDev);
      const response = await fetch(config.input, config.init);
      return await interceptResponse(response);
    } catch (error) {
      return handleFetchError(error as Error);
    }
  };
};
